import React, { useState, useCallback, useMemo } from "react";
import queryString from "query-string";
import { useHistory } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { VbzButton, VbzInputText } from "@smiles/voebiz-ui-kit";
import { cnpj } from "cpf-cnpj-validator";
import ReCAPTCHA from "react-google-recaptcha";

import { useUser } from "../../../context/UserContext";

import { RegisterContent, ModalError } from "../../../components";

import { validateCNPJ } from "../../../services/validateCNPJ";

import { removeMask, handleChangeDocumentCnpj } from "../../../utils/documentUtils";

const Cnpj: React.FC = () => {
  const ldFlags = useFlags();
  const urlParams = useMemo(() => queryString.parse(window.location.search), []);
  const { loginWithRedirect } = useAuth0();
  const { data, handler } = useUser();
  const [document, setDocument] = useState("");
  const [errorType, setErrorType] = useState("already_registered_error");
  const [isError, setIsError] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState("");
  const history = useHistory();
  const { t } = useTranslation("cnpj");

  const validateCnpjFormat = useCallback(() => {
    setValidated(cnpj.isValid(document));
    return cnpj.isValid(document);
  }, [document]);
  const login = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    loginWithRedirect({
      redirectUri: `${window.location.origin}/validar`,
    });
    return undefined;
  };
  const returnFromError = () => {
    setIsError(false);
    setIsLoading(false);
    setValidated(false);
  };
  const getError = () => {
    if (errorType === "already_registered_error") {
      return {
        errorTitle: t("already_registered_CNPJ_error"),
        errorMsg: t("already_registered_CNPJ_description"),
        onClickSecondary: () => {
          setErrorType("possible_reasons_error");
        },
        btnTextPrimary: t("try_again"),
        btnTextSecondary: t("possible_reasons_link"),
        btnIdSecondary: "btn_possibleReasons",
        handleCloseModal: () => {
          returnFromError();
        },
        onClickPrimary: () => {
          returnFromError();
        },
      };
    }
    if (errorType === "possible_reasons_error") {
      return {
        errorTitle: t("possible_reasons_error"),
        errorMsg: (
          <div>
            <strong>O CNPJ está incorreto:</strong>
            <span>
              <br /> Verifique se você digitou o número correto e tente de novo
            </span>
            <strong>
              <br />
              <br />O CNPJ já foi cadastrado:
            </strong>
            <span>
              <br />
              Verifique se a empresa já possui cadastro no VoeBiz e{" "}
              <a
                onClick={(e) => {
                  login(e);
                }}
              >
                {" "}
                acesse a conta{" "}
              </a>
              <br />
            </span>
            <strong>
              <br />O CPF ou e-mail já foi cadastrado:
            </strong>
            <span>
              <br />
              Verifique se o CPF ou e-mail do gestor da conta já estão cadastrados em outra conta.
            </span>
            <strong>
              <br />
              <br />
              <br />
              <br />
              Ainda precisa de ajuda?{" "}
            </strong>
            <span>
              Entre em contato com a <a href="/atendimento"> Central de Atendimento VoeBiz. </a>
            </span>
          </div>
        ),
        btnTextPrimary: t("try_again"),
        handleCloseModal: () => {
          returnFromError();
        },
        onClickPrimary: () => {
          returnFromError();
        },
      };
    }
    return {
      errorTitle: t("generic_error"),
      idTitle: "lbl_titleCnpjExisting",
      errorMsg: t("generic_error_description"),
      idMessage: "lbl_msgInformativeCnpjExisting_1",
      handleCloseModal: () => {
        returnFromError();
      },
      onClickPrimary: () => {
        returnFromError();
      },
      btnTextPrimary: t("try_to_register"),
      btnPrimaryColor: "primary",
      btnIdPrimary: "btn_retrievePassword",
    };
  };
  const modalErrorData = useMemo(() => getError(), [errorType]);
  const handleContinue = async () => {
    setIsLoading(true);
    const documentValue = removeMask(document);
    const recResponse = recaptchaValue;
    // comentar pra rodar local
    const { apiDomain } = ldFlags;
    // const apiDomain = "https://apigw-voebiz-hml5.smiles.com.br";

    const validateDocumentsResponse: any = await validateCNPJ("CNPJ", documentValue, recResponse, apiDomain);
    if (validateDocumentsResponse.status === 200) {
      if (validateDocumentsResponse.data.message.redirect) {
        setErrorType("already_registered_error");
        setIsError(true);
        setDocument("");
        setIsLoading(false);
      } else {
        handler({
          ...data,
          apiDomain,
          documentList: [
            {
              type: "CNPJ",
              preferential: true,
              number: document,
            },
            {
              type: "CPF",
              preferential: false,
              number: "",
            },
            {
              type: "RG",
              preferential: false,
              number: "",
            },
          ],
          smilesToken: validateDocumentsResponse.headers[`x-smiles-token`],
        });
        setIsLoading(false);
        history.push(`/cadastro/empresa`);
      }
    }
    setErrorType("already_registered_error");
    setIsError(true);
    setDocument("");
    setIsLoading(false);
  };
  return (
    <>
      <form className="vbz-lf vbz-cpf" onSubmit={handleContinue}>
        <div className="voebiz-register-cnpj voebiz-register-cnpj-section-1">
          <h3 className="voebiz-title-wrapper" id="lbl_titleCnpj">
            {t("title")}
          </h3>
          <div className="voebiz-content-wrapper">
            <RegisterContent idMessage="lbl_msgInformativeCnpj_1" text={t("description")} label={t("label")}>
              <div className="voebiz-cnpj">
                <div className="voebiz-cnpj_form_fadeIn-late">
                  <VbzInputText
                    id="inp_cnpj"
                    data-testid="inp_cnpj"
                    placeholder="Digite o CNPJ"
                    onChange={(e) => handleChangeDocumentCnpj(e, setDocument, setRecaptchaValue)}
                    value={document}
                    maxLength={18}
                    validate={validateCnpjFormat}
                    errorMessage="O CNPJ informado não é válido. Por favor, tente de novo."
                    inputMode="numeric"
                    regex={/[^0-9./-]/gi}
                  />
                </div>
                <div className={`voebiz-cnpj_form_fadeIn-latest recaptcha ${!validated && "disabled"}`}>
                  {validated && ldFlags.recaptchaSiteKey && (
                    <ReCAPTCHA
                      data-testid="recaptcha"
                      onExpired={() => {
                        setRecaptchaValue("");
                      }}
                      sitekey={ldFlags.recaptchaSiteKey}
                      onChange={(value) => {
                        setRecaptchaValue(value || "");
                      }}
                    />
                  )}
                </div>
                <div className="voebiz-cnpj_form_row fadeIn-latest">
                  <VbzButton
                    className="voebiz-cnpj_form_row_button"
                    text={t("continue_register")}
                    onClick={handleContinue}
                    onMouseDown={handleContinue}
                    isLoading={isLoading}
                    disabled={!validated ? true : !urlParams.bpr && recaptchaValue === ""}
                  />
                  <div className="voebiz-cnpj_form_row_access-account">
                    <span className="voebiz-cnpj_form_row_access-account_text">{t("access_account")}</span>
                    <VbzButton
                      className="voebiz-cnpj_form_row_access-account_link"
                      color="hyperlink"
                      text={t("access_account_link_CNPJ_page")}
                      onClick={login}
                    />
                  </div>
                </div>
              </div>
            </RegisterContent>
          </div>
        </div>
        <ModalError
          {...modalErrorData}
          isOpen={isError}
          onClosed={() => {
            setDocument("");
            setIsLoading(false);
          }}
          btnPrimaryColor={
            modalErrorData.btnPrimaryColor && modalErrorData.btnPrimaryColor === "secondary" ? "secondary" : "primary"
          }
        />
      </form>
    </>
  );
};
export default Cnpj;
